import { useState, useEffect, useCallback } from 'react';

let workerInstance = null;
let messageIdCounter = 0;
const pendingPromises = new Map();
const readyResolvers = new Set();

function getWorker() {
    if (!workerInstance) {
        workerInstance = new Worker(
            new URL('../../../workers/opencv.worker.js', import.meta.url)
        );

        workerInstance.onmessage = (event) => {
            const { id, type, success, result, error } = event.data;

            if (type === 'OPENCV_READY') {
                console.log('🎉 [Hook] OPENCV_READY:', result);
                readyResolvers.forEach(fn => fn(result));
                readyResolvers.clear();
                return;
            }

            const pending = pendingPromises.get(id);
            if (!pending) return;
            if (pending.timeoutId) clearTimeout(pending.timeoutId);
            pendingPromises.delete(id);
            if (success) pending.resolve(result);
            else pending.reject(new Error(error || 'Worker error'));
        };

        workerInstance.onerror = (e) => {
            console.error('❌ [Worker] error:', e.message);
        };
    }
    return workerInstance;
}

function sendMessage(type, payload, timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
        const worker = getWorker();
        const id = ++messageIdCounter;

        const timeoutId = setTimeout(() => {
            if (pendingPromises.has(id)) {
                pendingPromises.delete(id);
                reject(new Error(`Timeout: ${type} (id=${id})`));
            }
        }, timeoutMs);

        pendingPromises.set(id, { resolve, reject, timeoutId });

        try {
            worker.postMessage({ id, type, payload });
        } catch (err) {
            clearTimeout(timeoutId);
            pendingPromises.delete(id);
            reject(err);
        }
    });
}

function waitForReady(signal, timeoutMs = 60000) {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) return reject(new Error('Скасовано'));

        // Спершу запитуємо — може вже готовий
        sendMessage('GET_INFO', {})
            .then(info => {
                if (info.ready) {
                    resolve(info);
                    return;
                }
                // Не готовий — чекаємо OPENCV_READY broadcast
                const timeoutId = setTimeout(() => {
                    readyResolvers.delete(onReady);
                    reject(new Error('OpenCV не завантажився за 60 секунд'));
                }, timeoutMs);

                function onReady(info) {
                    if (signal?.aborted) {
                        clearTimeout(timeoutId);
                        return;
                    }
                    clearTimeout(timeoutId);
                    resolve(info);
                }

                readyResolvers.add(onReady);

                signal?.addEventListener('abort', () => {
                    readyResolvers.delete(onReady);
                    clearTimeout(timeoutId);
                });
            })
            .catch(() => {
                // GET_INFO впав — чекаємо OPENCV_READY
                const timeoutId = setTimeout(() => {
                    readyResolvers.delete(onReady);
                    reject(new Error('OpenCV не завантажився за 60 секунд'));
                }, timeoutMs);

                function onReady(info) {
                    clearTimeout(timeoutId);
                    resolve(info);
                }

                readyResolvers.add(onReady);
            });
    });
}

export function useOpenCvWorker() {
    const [isReady, setIsReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [version, setVersion] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        getWorker(); // створюємо Worker одразу

        waitForReady(controller.signal)
            .then(info => {
                console.log({info});
                if (controller.signal.aborted) return;
                setIsReady(true);
                setVersion(info.version);
                setLoading(false);
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                setError(err.message);
                setLoading(false);
            });

        return () => controller.abort();
    }, []);

    const detectDocument = useCallback(async (imageDataBuffer, width, height) => {
        const result = await sendMessage('DETECT_DOCUMENT', { imageData: imageDataBuffer, width, height });
        if (result?.warped) {
            result.warped = new ImageData(
                new Uint8ClampedArray(result.warped.data),
                result.warped.width,
                result.warped.height
            );
        }
        return result;
    }, []);

    const validateDocument = useCallback(async (imageDataBuffer, width, height) => {
        return sendMessage('VALIDATE_DOCUMENT', { imageData: imageDataBuffer, width, height });
    }, []);

    const reloadOpenCv = useCallback(async () => {
        setIsReady(false);
        setLoading(true);
        setError(null);
        await sendMessage('RELOAD_OPENCV', {});
        const controller = new AbortController();
        const info = await waitForReady(controller.signal);
        setIsReady(true);
        setVersion(info.version);
        setLoading(false);
    }, []);

    return { isReady, loading, error, version, detectDocument, validateDocument, reloadOpenCv };
}